import { UserInfo, UserType } from '@amzn/awscat-portal-authentication-library';
import { PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AAMUserProfile, UserAccessStatus, UserPermission, UserProfile } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType,withAuthContext,withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Badge, Button, Checkbox, FormField, Grid, Input, InputProps,
    NonCancelableCustomEvent, Select, SelectProps, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';
import { FunctionComponent, useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { CAT_PORTAL_PREFIX, CATDEV_NAME, CATDEV_PROVIDER, format,getAuthProvider, isCognito } from '../../common/util';
import ConfirmationModal from '../common/ConfirmationModal';
import { updateQuestionnairesOwners } from '../portfolio-mgmt/data-collection/data-collection-helper';
import { userAccessStatusDisplay, userActionOptions, UserPermissionAccess } from './constants';
import { PortfolioViewModel } from './interface';
import { currentStatus, validateEmail } from './util';
interface MultipleEmailState {
    usersFound: UserPermission[];
    visible: boolean;
}
interface Props {
    selectedPortfolio: PortfolioViewModel;
    closeModal: () => void;
}
const portfolioService = new PortfolioService();
const SharePortfolioModal: FunctionComponent<Props & FlashContextInterface & AuthContextInterface> = (
    {flash, selectedPortfolio, auth, closeModal}): JSX.Element => {
    const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserAction, setNewUserAction] = useState<SelectProps.Option | null>(userActionOptions[0]);
    const [shareButtonDisabled, setShareButtonDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({
        header: '',
        message: '',
        visible: false,
    });
    const [multipleEmail, setMultipleEmail] = useState<MultipleEmailState>({
        usersFound: [],
        visible: false,
    });
    const [nonexistentEmailVisible, setNonexistentEmailVisible] = useState(false);
    const [discardChangesVisible, setDiscardChangesVisible] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    
    useEffect(() => {
        if (!selectedPortfolio) {
            return;
        }
        async function getUserProfiles(principalIds: string[]): Promise<UserProfile[]> {
            if (!principalIds.length) {
                return [];
            }
            if (getAuthProvider() === CATDEV_PROVIDER) { 
                const aamProfile: AAMUserProfile = await portfolioService.verifyAppPermission();
                return [{
                    email: aamProfile.email,
                    userId: aamProfile.userID,
                    name: CATDEV_PROVIDER,
                    organizationId: CATDEV_NAME,
                    organizationName: CATDEV_NAME,
                    type: CATDEV_NAME,
                    displayName: CATDEV_NAME,
                }];
            } else {
                const userIds = principalIds.map(id => id.replace(CAT_PORTAL_PREFIX, ''));
                const sharedUserProfiles: UserProfile[] = await auth.getUserInfoList(userIds);
                return sharedUserProfiles.map(profile => ({...profile, principalID: CAT_PORTAL_PREFIX + profile.userId}));
            }
        }
        async function getPortfolioPermissions(id: string) {
            setLoading(true);
            try {
                const userPerms: UserPermission[] = await portfolioService.getPortfolioPermissions(id);
                const principalIds: string[] = userPerms.filter(userPermission => !userPermission.permissionKey)
                    .map(userPermission => userPermission.principalID);
                const sharedUserProfiles = await getUserProfiles(principalIds);
                userPerms.map(userPermission => {
                    userPermission.status = currentStatus(userPermission);
                    if (userPermission.status === UserAccessStatus.INVITED) {
                        // use cat portal user profile if pending user already has one. This is when user already has
                        // permission but we still has a lingering pending permission.
                        userPermission.userProfile = sharedUserProfiles.find(userProfile =>
                            userProfile.email === userPermission.email && userProfile.organizationId === userPermission.organizationId)
                            || {email: userPermission.email} as UserProfile;
                    } else {
                        userPermission.userProfile = sharedUserProfiles.find(userProfile =>
                            userProfile.principalID === userPermission.principalID) || {email: userPermission.principalID} as UserProfile;   
                    }
                    userPermission.action = userPermission.action.trim();
                });
                setUserPermissions(userPerms);
            } catch (err) {
                flash.alert(FlashType.error, (err as Error).message, constants.UNABLE_TO_GET_ACCESS_PERMISSIONS);
                closeModal();
                return;
            }
            setLoading(false);
        }
        getPortfolioPermissions(selectedPortfolio.portfolioId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function addPortfolioPermission(id: string, name: string, accountName: string, principalID: string, action: string, email: string) {
        try {
            await portfolioService.addPortfolioPermission(id, name, accountName, principalID, action, email);
            await updateQuestionnairesOwners(selectedPortfolio.portfolioId, email);
            flash.alert(FlashType.success, constants.ACCESS_GRANTED_TO + email, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.UNABLE_TO_GRANT_ACCESS);
        }
    }
    async function deletePendingPermission(id: string, permissionKey: string, email: string) {
        try {
            await portfolioService.deletePendingPermission(id, permissionKey);
            await updateQuestionnairesOwners(selectedPortfolio.portfolioId, email, false);
            flash.alert(FlashType.success, format(constants.INVITATION_CANCELED, [email]), '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.UNABLE_TO_CANCEL);
        }
    }
    async function deletePortfolioPermission(id: string, principalID: string, action: string, email: string) {
        try {
            await portfolioService.deletePortfolioPermission(id, principalID, action);
            await updateQuestionnairesOwners(selectedPortfolio.portfolioId, email, false);
            flash.alert(FlashType.success, constants.ACCESS_REVOKED + email, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.UNABLE_TO_REVOKE);
        }
    }
    async function inviteUserPortfolioPermission(id: string, name: string, email: string, action: string) {
        try {
            await portfolioService.inviteUserPortfolioPermission(id, name, name, email, action);
            await updateQuestionnairesOwners(selectedPortfolio.portfolioId, email);
            flash.alert(FlashType.success, constants.INVITATION_SENT_TO + email, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.UNABLE_TO_INVITE);
        }
    }
    const onConfirmShare = async () => {
        if (!selectedPortfolio) {
            return;
        }
        const { portfolioId, portfolioName, accountName } = selectedPortfolio;
        setLoading(true);
        await Promise.all(userPermissions.map(async userPermission => {
            const { action, permissionKey, principalID, userProfile } = userPermission;
            if (!userProfile) {
                return;
            }
            switch (userPermission.status) {
                case UserAccessStatus.ADD:
                    return addPortfolioPermission(portfolioId, portfolioName, accountName, principalID, action, userProfile.email);
                case UserAccessStatus.DELETE:
                    if (permissionKey) {
                        return deletePendingPermission(portfolioId, permissionKey, userProfile.email);
                    } else {
                        return deletePortfolioPermission(portfolioId, principalID, action, userProfile.email);
                    }
                case UserAccessStatus.INVITE:
                    return inviteUserPortfolioPermission(portfolioId, portfolioName, userProfile.email, action);
            }
        }));
        setLoading(false);
        closeModal();
    };
    const isLastExistingFullAccess = (userPermission: UserPermission): boolean => {
        return userPermission.action === UserPermissionAccess.FULL && !userPermissions.some(userPerm => {
            return userPerm.action === UserPermissionAccess.FULL && userPerm.status === UserAccessStatus.EXISTING
                && userPerm.principalID !== userPermission.principalID;
        });
    };
    const addUsersInListIfInexistent = (userPermsToAdd: UserPermission[]) => {
        const userPermissionsToAdd: UserPermission[] = [];
        userPermsToAdd.forEach(userPermission => {
            const exist = userPermissions.find(userPerm =>
                userPerm.principalID === userPermission.principalID && userPerm.action === userPermission.action);
            if (exist) {
                setAlert({
                    header: constants.ACCESS_ALREADY_GRANTED,
                    message: (userPermission.action === UserPermissionAccess.FULL ? constants.FULL
                        : userPermission.action) + constants.ACCESS_ALREADY_GRANTED_TO + userPermission.userProfile?.email,
                    visible: true,
                });
            } else {
                userPermissionsToAdd.push(userPermission);
            }
        });
        if (userPermissionsToAdd.length) {
            setShareButtonDisabled(false);
            setUserPermissions([...userPermissions, ...userPermissionsToAdd]);
        }
    };

    const removePermission = (userPermission: UserPermission) => {
        setAlert({...alert, visible: false});
        const newPermissions = [...userPermissions];
        if (userPermission.status === UserAccessStatus.EXISTING || userPermission.status === UserAccessStatus.INVITED) {
            if (isLastExistingFullAccess(userPermission)) {
                setAlert({
                    header: constants.UNABLE_TO_REVOKE,
                    message: constants.UNABLE_TO_REVOKE_LAST_EXISTING_FULL_ACCESS,
                    visible: true,
                });
                return;
            }
            userPermission.status = UserAccessStatus.DELETE;
        } else if (userPermission.status === UserAccessStatus.DELETE) {
            userPermission.status = currentStatus(userPermission);
        } else {
            const index = userPermissions.indexOf(userPermission);
            newPermissions.splice(index, 1);
        }
        const isPristine = newPermissions.every(permission => {
            return permission.status === UserAccessStatus.EXISTING || permission.status === UserAccessStatus.INVITED;
        });
        setShareButtonDisabled(isPristine);
        setUserPermissions(newPermissions);
    };

    const resetNewPermissionValues = () => {
        setNewUserEmail('');
        setNewUserAction(null);
    };
    const addPermission = async () => {
        setAlert({...alert, visible: false});
        let userInfos: UserInfo[] = [];
        try {
            userInfos = await auth.searchUserInfoByEmail(newUserEmail);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.FAIL_TO_SEARCH_USER_BY_EMAIL);
        }
        const userProfiles: UserProfile[] = userInfos.map(user => ({
            userId: user.userId,
            displayName: user.displayName,
            email: user.email,
            organizationId: user.organizationId,
            organizationName: user.organizationName,
            name: user.name,
            type: user.type,
            principalID: `${CAT_PORTAL_PREFIX}${user.userId}`,
        }));
        if (userProfiles?.length > 0) {
            // Grant permission to all users with exact match email as Cognito user retrieves limited attribute in user profiles
            if (await isCognito(auth)) {
                userProfiles.forEach(userProfile => {
                    addUsersInListIfInexistent([{
                        status: UserAccessStatus.ADD,
                        action: newUserAction?.value || '',
                        principalID: userProfile.principalID || '',
                        userProfile,
                    }]);
                });
                resetNewPermissionValues();
            } else if (userProfiles.length === 1) {
                const userProfile = userProfiles[0];
                addUsersInListIfInexistent([{
                    status: UserAccessStatus.ADD,
                    action: newUserAction?.value || '',
                    principalID: userProfile.principalID || '',
                    userProfile: userProfile,
                }]);
                resetNewPermissionValues();
            } else {
                // multiple userProfiles tie to the same email
                const usersFound: UserPermission[] = [];
                userProfiles.forEach(userProfile => {
                    usersFound.push({
                        status: UserAccessStatus.ADD,
                        action: newUserAction?.value || '',
                        principalID: userProfile.principalID || '',
                        userProfile: userProfile,
                    });
                });
                setMultipleEmail({
                    usersFound,
                    visible: true,
                });
            }
        } else {
            // didn't find userProfile corresponding to the email in catportal
            setNonexistentEmailVisible(true);
        }
    };

    const colDefs: TableProps.ColumnDefinition<UserPermission>[] = [{
        header: constants.STATUS,
        id: 'status',
        cell: e => {
            const status = userAccessStatusDisplay[e.status];
            return <Badge color={status.badgeColor}>{status.text}</Badge>;
        },
    }, {
        header: constants.EMAIL,
        id: 'email',
        cell: e => e.userProfile?.email,
    }, {
        header: constants.ACTION,
        id: 'action',
        cell: e => e.action === UserPermissionAccess.FULL ? constants.FULL : e.action,
    }, {
        header: constants.REMOVE_BUTTON_TEXT,
        id: 'remove',
        cell: e => (
            <Button iconName='close' variant='icon' onClick={() => removePermission(e)}></Button>
        ),
    }];
    const nonexistentEmailSend = () => {
        const newUserPermission: UserPermission = {
            status: UserAccessStatus.INVITE,
            action: newUserAction?.value || '',
            userProfile: {
                email: newUserEmail,
            } as UserProfile,
            principalID: '',
        };
        setUserPermissions([...userPermissions, newUserPermission]);
        setShareButtonDisabled(false);
        resetNewPermissionValues();
        setNonexistentEmailVisible(false);
    };
    const multipleEmailAdd = () => {
        addUsersInListIfInexistent(multipleEmail.usersFound.filter(userPermission => userPermission.toAdd));
        resetNewPermissionValues();
        setMultipleEmail({...multipleEmail, visible: false});
    };
    const onDismissMultipleEmail = () => {
        resetNewPermissionValues();
        setMultipleEmail({...multipleEmail, visible: false});
    };
    const multipleUserColDefs: TableProps.ColumnDefinition<UserPermission>[] = [{
        header: '',
        id: 'toAdd',
        cell: e => {
            return (
                <Checkbox onChange={event => {
                    const newUsersFound = [...multipleEmail.usersFound];
                    newUsersFound[newUsersFound.indexOf(e)].toAdd = event.detail.checked;
                    setMultipleEmail({...multipleEmail, usersFound: newUsersFound});
                }} checked={e.toAdd || false}></Checkbox>
            );
        },
    }, {
        header: constants.NAME,
        id: 'name',
        cell: e => e.userProfile?.name || e.userProfile?.email,
    }, {
        header: constants.ORGANIZATION,
        id: 'organization',
        cell: e => e.userProfile?.type === UserType.COGNITO ? 'Cognito': e.userProfile?.organizationName,
    }];
    const onEmailChange = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
        const newUserEmail = event.detail.value;
        setEmailErrorMessage(validateEmail(newUserEmail));
        setNewUserEmail(newUserEmail);
    };
    return (
        <ConfirmationModal
            visible
            header={`${constants.SHARE_BUTTON_TEXT} ${selectedPortfolio?.portfolioName}`}
            onConfirm={onConfirmShare}
            onDismiss={() => {
                if (!shareButtonDisabled) {
                    setDiscardChangesVisible(true);
                } else {
                    closeModal();
                }
            }}
            confirmDisabled={shareButtonDisabled}
            confirmLoading={loading}
            confirmButton={constants.APPLY_BUTTON_TEXT} >
            <Alert
                type='error'
                header={alert.header}
                visible={alert.visible}
                dismissible
                onDismiss={() => setAlert({visible: false, header: '', message: ''})}>{alert.message}</Alert>
            <SpaceBetween size='xs'>
                <b>{constants.USER_ACCESS}</b>
                <Table
                    loading={loading}
                    loadingText={constants.LOADING_USER_ACCESS}
                    columnDefinitions={colDefs}
                    items={userPermissions} />
                <span>{constants.NEW_USER_ACCESS}</span>
                <Grid gridDefinition={[{ colspan: 7 }, { colspan: 3 }, { colspan: 2 }]}>
                    <FormField errorText={emailErrorMessage}>
                        <Input
                            invalid={!!emailErrorMessage}
                            placeholder={constants.EMAIL_PLACEHOLDER}
                            onChange={onEmailChange}
                            value={newUserEmail} />
                    </FormField>
                    <Select
                        placeholder={constants.ACTION}
                        onChange={e => setNewUserAction(e.detail.selectedOption)}
                        options={userActionOptions} selectedOption={newUserAction} />
                    <Button onClick={addPermission} disabled={!newUserEmail || !newUserAction || !!emailErrorMessage}>{constants.ADD_BUTTON_TEXT}</Button>
                </Grid>
                <Alert type='info' dismissible={false} header={constants.EMAIL_IS_THE_EXPECTED_ONE}>
                    {constants.EMAIL_ALERT_INFO}
                </Alert>
            </SpaceBetween>
            <ConfirmationModal
                visible={nonexistentEmailVisible}
                header={`${newUserEmail}${constants.EMAIL_NOT_FOUND}`}
                onDismiss={() => setNonexistentEmailVisible(false)}
                onConfirm={() => nonexistentEmailSend()}>
                <span>{format(constants.WOULD_YOU_LIKE_TO_INVITE, [newUserEmail, selectedPortfolio?.portfolioName])}</span>
            </ConfirmationModal>
            <ConfirmationModal
                visible={discardChangesVisible}
                header={constants.DISCARD_CHANGES}
                onDismiss={() => setDiscardChangesVisible(false)}
                onConfirm={() => {
                    setDiscardChangesVisible(false);
                    closeModal();
                }}>
                <span>{constants.DISCARD_CHANGES_ALERT}</span>
            </ConfirmationModal>
            <ConfirmationModal
                visible={multipleEmail.visible}
                header={`${constants.MULTIPLE_EMAIL_FOUND}${newUserEmail}`}
                onDismiss={onDismissMultipleEmail}
                onConfirm={() => multipleEmailAdd()}>
                <span>{constants.MULTIPLE_EMAIL_PLEASE_SELECT}'{selectedPortfolio?.portfolioName}'?</span>
                <Table loading={loading} columnDefinitions={multipleUserColDefs} items={multipleEmail.usersFound}></Table>
            </ConfirmationModal>
        </ConfirmationModal>
    );
};

export default withAuthContext(withFlashContext(SharePortfolioModal));
